const STORAGE_KEY = 'theme';
const VALID_THEMES = ['light', 'dark', 'system'];

export const THEME_MODE = {
  LIGHT: 0,
  DARK: 1,
  SYSTEM: 2,
};

let mediaQuery = null;
let mediaListener = null;
let resumeListenersBound = false;

export function isLightThemeForced() {
  return window.__forceLightTheme === true;
}

export function themeModeToStored(mode) {
  const value = Number(mode);

  if (value === THEME_MODE.DARK) {
    return 'dark';
  }

  if (value === THEME_MODE.LIGHT) {
    return 'light';
  }

  return 'system';
}

export function storedToThemeMode(stored) {
  if (stored === 'dark') {
    return THEME_MODE.DARK;
  }

  if (stored === 'light') {
    return THEME_MODE.LIGHT;
  }

  return THEME_MODE.SYSTEM;
}

export function getThemeModeFromUser() {
  if (typeof window.__userThemeMode === 'number') {
    return window.__userThemeMode;
  }

  return null;
}

export function getStoredTheme() {
  if (isLightThemeForced()) {
    return 'light';
  }

  const userMode = getThemeModeFromUser();

  if (userMode !== null) {
    return themeModeToStored(userMode);
  }

  const stored = localStorage.getItem(STORAGE_KEY) || 'system';
  return VALID_THEMES.includes(stored) ? stored : 'system';
}

export function getEffectiveTheme(stored = getStoredTheme()) {
  if (stored === 'dark') {
    return 'dark';
  }
  if (stored === 'light') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(stored = getStoredTheme()) {
  const effective = isLightThemeForced() ? 'light' : getEffectiveTheme(stored);
  document.documentElement.setAttribute('data-bs-theme', effective);
  document.body?.setAttribute('data-theme', effective);
  window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: effective } }));
}

export function setTheme(stored) {
  if (isLightThemeForced()) {
    applyTheme('light');
    return;
  }

  localStorage.setItem(STORAGE_KEY, stored);
  applyTheme(stored);
  bindSystemListener(stored);
}

export function setThemeFromMode(mode) {
  window.__userThemeMode = Number(mode);
  setTheme(themeModeToStored(mode));
}

function refreshSystemTheme() {
  if (getStoredTheme() === 'system') applyTheme('system');
}

function bindSystemListener(stored = getStoredTheme()) {
  // Mobile browsers may suspend the page through a scheduled appearance change.
  // Re-read the preference when returning, even if no media-query event arrived.
  if (!resumeListenersBound) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshSystemTheme();
    });
    window.addEventListener('pageshow', refreshSystemTheme);
    window.addEventListener('focus', refreshSystemTheme);
    resumeListenersBound = true;
  }
  if (!mediaQuery) {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaListener = refreshSystemTheme;
  }

  if (stored === 'system') {
    if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', mediaListener);
    else mediaQuery.addListener(mediaListener);
  } else {
    if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', mediaListener);
    else mediaQuery.removeListener(mediaListener);
  }
}

export function initTheme() {
  if (isLightThemeForced()) {
    applyTheme('light');
    bindSystemListener('light');
    return;
  }

  const userMode = getThemeModeFromUser();

  if (userMode !== null) {
    localStorage.setItem(STORAGE_KEY, themeModeToStored(userMode));
  }

  applyTheme();
  bindSystemListener();
}
